// useGuideSeo.js
// Injects <title>, meta description, canonical, Open Graph / Twitter tags, and
// Article JSON-LD for a guide page. Uses the SAME document.querySelector /
// createElement technique already used in LandingPage.js (no react-helmet
// dependency added). Call once per article via the GuideArticle template.
//
// On unmount it does NOT tear tags down — the next routed page (landing,
// another guide) overwrites them with its own values, same as the existing
// landing-page behavior.

import { useEffect } from 'react';

const SITE = 'https://teetimeorganizer.net';

function setMetaByName(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

function setMetaByProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setArticleJsonLd(guide, url) {
  const id = 'guide-article-jsonld';
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.metaDescription,
    datePublished: guide.datePublished,
    dateModified: guide.dateModified,
    author: { '@type': 'Organization', name: 'Tee Time Organizer' },
    publisher: {
      '@type': 'Organization',
      name: 'Tee Time Organizer',
      url: SITE,
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  });
}

function setBreadcrumbJsonLd(guide, url) {
  const id = 'guide-breadcrumb-jsonld';
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${SITE}/guides` },
      { '@type': 'ListItem', position: 3, name: guide.title, item: url },
    ],
  });
}

export function useGuideSeo(guide) {
  useEffect(() => {
    if (!guide) return;
    const url = `${SITE}/guides/${guide.slug}`;

    document.title = `${guide.title} | Tee Time Organizer`;
    setMetaByName('description', guide.metaDescription);
    setCanonical(url);

    setMetaByProperty('og:type', 'article');
    setMetaByProperty('og:title', guide.title);
    setMetaByProperty('og:description', guide.metaDescription);
    setMetaByProperty('og:url', url);

    setMetaByName('twitter:card', 'summary_large_image');
    setMetaByName('twitter:title', guide.title);
    setMetaByName('twitter:description', guide.metaDescription);

    setArticleJsonLd(guide, url);
    setBreadcrumbJsonLd(guide, url);
  }, [guide]);
}
